import { CommonModule } from '@angular/common';
import { Component, DEFAULT_CURRENCY_CODE, inject, type OnDestroy } from '@angular/core';
import { FormBuilder, type FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  type Observable,
  tap,
} from 'rxjs';
import { SubSink } from 'subsink';
import type { CreateEduBudgetCorrection } from '../../shared/models/api/edu-budget-correction.model';
import type { ActiveUser } from '../../shared/models/api/user.model';
import type { ControlsOf } from '../../shared/models/controls-of.model';
import { EduBudgetCorrectionService } from '../../shared/services/api/edu-budget-correction.service';
import { UserService } from '../../shared/services/api/user.service';
import { OpaValidators } from '../../shared/validators/opa.validators';
import type {
  CreateEduBudgetCorrectionData,
  CreateEduBudgetCorrectionFormValue,
} from './edu-budget-correction-create.models';

@Component({
  selector: 'opa-edu-budget-correction-create',
  imports: [CommonModule, PorscheDesignSystemModule, ReactiveFormsModule, LetDirective],
  templateUrl: './edu-budget-correction-create.component.html',
  styleUrl: './edu-budget-correction-create.component.scss',
})
export class EduBudgetCorrectionCreateComponent implements OnDestroy {
  private readonly formBuilder = inject(FormBuilder).nonNullable;
  private readonly userService = inject(UserService);
  private readonly eduBudgetCorrectionService = inject(EduBudgetCorrectionService);
  private readonly toastManager = inject(ToastManager);
  private readonly router = inject(Router);
  readonly defaultCurrencyCode = inject(DEFAULT_CURRENCY_CODE);

  private readonly subSink = new SubSink();
  private readonly submitting = new BehaviorSubject(false);
  readonly form = this.buildForm();
  readonly textareaMaxLength = 255;
  createEduBudgetCorrectionData$ = this.getCreateEduBudgetCorrectionData$();

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  reloadCreateEduBudgetCorrectionData(): void {
    this.createEduBudgetCorrectionData$ = this.getCreateEduBudgetCorrectionData$();
  }

  trackUser(index: number, user: ActiveUser): number {
    return user.id;
  }

  submit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.next(true);
    const formValue = this.form.getRawValue();
    const eduBudgetCorrection = this.formValueToEduBudgetCorrection(formValue);
    this.subSink.sink = this.eduBudgetCorrectionService
      .createEduBudgetCorrection(eduBudgetCorrection)
      .pipe(
        tap({
          next: () => {
            this.toastManager.addMessage({
              text: `Education budget correction created`,
              state: 'success',
            });
            this.router.navigate(['/edu-budget-corrections']);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }

  private buildForm(): FormGroup<ControlsOf<CreateEduBudgetCorrectionFormValue>> {
    const form = this.formBuilder.group<ControlsOf<CreateEduBudgetCorrectionFormValue>>({
      userId: this.formBuilder.control(null, OpaValidators.required()),
      amount: this.formBuilder.control(null, [
        OpaValidators.required(),
        OpaValidators.maxDecimals(2),
      ]),
      description: this.formBuilder.control(null, OpaValidators.maxLength(this.textareaMaxLength)),
    });
    return form;
  }

  private formValueToEduBudgetCorrection(
    form: CreateEduBudgetCorrectionFormValue,
  ): CreateEduBudgetCorrection {
    const eduBudgetCorrection: CreateEduBudgetCorrection = {
      userId: Number(form.userId!),
      amount: form.amount!,
      description: form.description ?? '',
    };
    return eduBudgetCorrection;
  }

  private getCreateEduBudgetCorrectionData$(): Observable<CreateEduBudgetCorrectionData> {
    const users$ = this.userService.getActiveUsers();
    const submitting$ = this.submitting.pipe(distinctUntilChanged());
    return combineLatest([users$, submitting$]).pipe(
      map(([users, submitting]) => ({ users, submitting })),
    );
  }
}
